import { PlasticsManufacturingRatesVersionStatus } from "../enums/plastics-manufacturing-rates-version-status.enum";
import { PLASTICS_MANUFACTURING_RATES_PARENT_ID } from "./plastics-manufacturing-rates-parent.model";

export type PlasticsMaterialCostCategoryKey =
    'naturalPe'
    | 'blackRepro'
    | 'dryColoredPe'
    | 'preCompoundedColor'
    | 'granites'
    | 'xlpe'
    | 'flameRetardant'
    | 'other';

export interface PlasticsMaterialCategoryRate {
    key: PlasticsMaterialCostCategoryKey;
    label: string;
    costPerPound: number;
}

/**
 * The resin names AS THEY APPEAR ON A QUOTE. These are the only names: the shop's
 * shorthand - "black repro", "XLPE" - is not carried anywhere, because a name
 * that exists in two forms eventually goes out to a customer in the wrong one.
 */
export const PLASTICS_MATERIAL_COST_CATEGORY_LABELS: Record<PlasticsMaterialCostCategoryKey, string> = {
    naturalPe: 'Natural Polyethylene',
    blackRepro: 'Black Reprocess Polyethylene',
    dryColoredPe: 'Dry-Colored Polyethylene',
    preCompoundedColor: 'Pre-Compounded Colored Polyethylene',
    granites: 'Granite Blend Polyethylene',
    xlpe: 'Cross-Linked Polyethylene (XLPE)',
    flameRetardant: 'Flame-Retardant Polyethylene',
    other: 'Other Polyethylene',
};

export const PLASTICS_MATERIAL_COST_CATEGORY_KEYS: PlasticsMaterialCostCategoryKey[] = [
    'naturalPe',
    'blackRepro',
    'dryColoredPe',
    'preCompoundedColor',
    'granites',
    'xlpe',
    'flameRetardant',
    'other',
];

export const DEFAULT_PLASTICS_MATERIAL_CATEGORY_RATES: PlasticsMaterialCategoryRate[] = [
    { key: 'naturalPe', label: PLASTICS_MATERIAL_COST_CATEGORY_LABELS.naturalPe, costPerPound: 0.90 },
    { key: 'blackRepro', label: PLASTICS_MATERIAL_COST_CATEGORY_LABELS.blackRepro, costPerPound: 0.80 },
    { key: 'dryColoredPe', label: PLASTICS_MATERIAL_COST_CATEGORY_LABELS.dryColoredPe, costPerPound: 0.97 },
    { key: 'preCompoundedColor', label: PLASTICS_MATERIAL_COST_CATEGORY_LABELS.preCompoundedColor, costPerPound: 1.64 },
    { key: 'granites', label: PLASTICS_MATERIAL_COST_CATEGORY_LABELS.granites, costPerPound: 0 },
    { key: 'xlpe', label: PLASTICS_MATERIAL_COST_CATEGORY_LABELS.xlpe, costPerPound: 1.87 },
    { key: 'flameRetardant', label: PLASTICS_MATERIAL_COST_CATEGORY_LABELS.flameRetardant, costPerPound: 3.00 },
    { key: 'other', label: PLASTICS_MATERIAL_COST_CATEGORY_LABELS.other, costPerPound: 0 },
];

export interface PlasticsManufacturingRatesVersion {
    id: string;
    manufacturingRatesId: string;
    name: string;
    status: PlasticsManufacturingRatesVersionStatus;
    versionNumber: number | null;

    largeMachineHourlyRate: number;
    largeMachineDefaultMinutes: number;
    smallMachineHourlyRate: number;
    smallMachineDefaultMinutes: number;

    /* Secondary operations, one field per operation the quote actually offers and
       nothing else. Hand routing, robot operation and install-items were rates the
       first cost model used; no quote has them, so they are gone. */
    basicTrimmingCostPerMinute: number;
    deflashingCostPerMinute: number;
    precisionTrimmingCostPerMinute: number;
    flamePolishCostPerMinute: number;
    assemblyCostPerMinute: number;
    packagingCostPerMinute: number;
    /** advanced quality checks and testing a customer asks for - leak tests, extra inspections */
    qualityTestingCostPerMinute: number;
    /** filling a part with two-part foam (insulated tanks and the like) */
    foamFillingCostPerMinute: number;
    otherCostPerMinute: number;
    /**
     * A flat $ PER MOLDED PART, added to anything actually molded and to nothing
     * else. This replaced the mold-release-per-gallon pair: consumables are not
     * worth metering per part, so mold release, vent tubing and the mold repair
     * fund are all carried here as one figure.
     */
    moldingFixedCostPerPart: number;
    /**
     * Market resin surcharge $/lb applied per pound of part shot weight. Floats with market —
     * quote lines multiply their shot-weight snapshot by the currently released rate.
     */
    resinSurchargePerPound: number;
    materialCategoryRates: PlasticsMaterialCategoryRate[];
    notes: string;

    createdById: string;
    createdOn: Date;
    updatedById: string;
    updatedOn: Date;
    releasedById: string | null;
    releasedOn: Date | null;
    releaseComment: string;
}

export const DEFAULT_PLASTICS_MANUFACTURING_RATES_VERSION: PlasticsManufacturingRatesVersion = {
    id: '',
    manufacturingRatesId: PLASTICS_MANUFACTURING_RATES_PARENT_ID,
    name: 'Manufacturing rates',
    status: PlasticsManufacturingRatesVersionStatus.Draft,
    versionNumber: null,

    largeMachineHourlyRate: 150,
    largeMachineDefaultMinutes: 20,
    smallMachineHourlyRate: 120,
    smallMachineDefaultMinutes: 20,

    basicTrimmingCostPerMinute: 1,
    deflashingCostPerMinute: 1,
    precisionTrimmingCostPerMinute: 1.25,
    flamePolishCostPerMinute: 1,
    assemblyCostPerMinute: 1,
    packagingCostPerMinute: 1,
    qualityTestingCostPerMinute: 1.5,
    foamFillingCostPerMinute: 2,
    otherCostPerMinute: 1,
    moldingFixedCostPerPart: 2,
    resinSurchargePerPound: 0.45,
    materialCategoryRates: DEFAULT_PLASTICS_MATERIAL_CATEGORY_RATES,
    notes: '',

    createdById: '',
    createdOn: null as unknown as Date,
    updatedById: '',
    updatedOn: null as unknown as Date,
    releasedById: null,
    releasedOn: null,
    releaseComment: '',
};

/**
 * The material rates a version carries, filled in from the defaults for anything
 * it does not name, and always labelled from the official names above so a stale
 * label stored on an old row can never reach a quote.
 */
export function getPlasticsMaterialCategoryRates(
    manufacturingRatesVersion: PlasticsManufacturingRatesVersion | null | undefined,
): PlasticsMaterialCategoryRate[] {
    const ratesByKey = new Map<PlasticsMaterialCostCategoryKey, PlasticsMaterialCategoryRate>(
        DEFAULT_PLASTICS_MATERIAL_CATEGORY_RATES.map(rate => [rate.key, rate]),
    );
    for (const rate of manufacturingRatesVersion?.materialCategoryRates ?? []) {
        if (!rate?.key || !(rate.key in PLASTICS_MATERIAL_COST_CATEGORY_LABELS)) {
            continue;
        }
        ratesByKey.set(rate.key, {
            key: rate.key,
            label: PLASTICS_MATERIAL_COST_CATEGORY_LABELS[rate.key],
            costPerPound: Math.max(0, Number(rate.costPerPound) || 0),
        });
    }
    return DEFAULT_PLASTICS_MATERIAL_CATEGORY_RATES.map(fallback => ratesByKey.get(fallback.key) ?? fallback);
}
